import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Search,
  ArrowUpDown,
  Download,
  ArrowRight,
  Clock,
  UserRound,
  AlertTriangle,
} from 'lucide-react';
import { avatarStyle, initials } from '../theme';
const BASE_URL = process.env.REACT_APP_BASE_URL;

/* GET /api/v1/agency/dashboard/funnel/stages/{stage_key}
   ?days=30&limit=25&page=1&search=&order=oldest|newest      (limit: 1–100) */
const FUNNEL_STAGE_ENDPOINT = (stageKey) =>
  `/api/v1/agency/dashboard/funnel/stages/${encodeURIComponent(stageKey)}`;

const LIMIT = 100; // API max — the list pages itself in on scroll
const SLA_RISK_DAYS = 30; // API has no SLA flag yet: days in stage is the proxy

/* Every allowed stage_key for the queue endpoint. Labels are resolved back to
   keys as well, so the drawer still calls a valid stage when it is handed a
   stage object without a key (e.g. the static fallback while the funnel loads). */
const FUNNEL_STAGE_KEYS = [
  'onboarding',
  'pending',
  'in_review',
  'resubmission',
  'approved',
];
const STAGE_KEY_BY_LABEL = {
  onboarding: 'onboarding',
  pending: 'pending',
  'in review': 'in_review',
  review: 'in_review',
  resubmission: 'resubmission',
  'sent for resubmission': 'resubmission',
  sent_for_resubmission: 'resubmission',
  approved: 'approved',
  approval: 'approved',
  approvals: 'approved',
};

const resolveStageKey = (stage) => {
  const raw = String(stage?.key || stage?.stage || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (FUNNEL_STAGE_KEYS.includes(raw)) return raw;
  return (
    STAGE_KEY_BY_LABEL[raw] ||
    STAGE_KEY_BY_LABEL[
      String(stage?.label || '')
        .trim()
        .toLowerCase()
    ] ||
    null
  );
};

/* days is only allowed to be 7, 30 or 90 */
const ALLOWED_DAYS = [7, 30, 90];
const safeDays = (value) =>
  ALLOWED_DAYS.includes(Number(value)) ? Number(value) : 30;

const PRIORITY_CHIP = {
  HIGH: 'text-[#DC2626] bg-[#FEF2F2] ring-1 ring-[#FECACA]',
  MEDIUM: 'text-[#D97706] bg-[#FFFBEB] ring-1 ring-[#FDE68A]',
  LOW: 'text-[#059669] bg-[#ECFDF5] ring-1 ring-[#A7F3D0]',
  BLOCKED: 'text-white bg-slate-800',
};

const fmtDays = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

/* API customer → the shape this drawer already renders. */
const mapCase = (c) => {
  const rawDays = Number(c.days_in_stage);
  const days = Number.isFinite(rawDays) ? rawDays : 0;
  return {
    id: c.client_id || `#${c.customer_user_id}`,
    name: c.full_name || '—',
    email: c.email_address || '',
    days,
    assignedMembershipId: c.assigned_membership_id ?? null,

    waitingSince: c.waiting_since,
    statusLabel: c.status?.label || '',
    sla: days >= SLA_RISK_DAYS,
  };
};

/* Slide-over queue for a single funnel stage.
   In your real app this same UI can back a route like
   /customer-list?stage=awaiting_customer */
export default function StageQueueDrawer({ stage, days = 30, onClose }) {
  const navigate = useNavigate();
  const stageKey = resolveStageKey(stage);
  const windowDays = safeDays(days);

  const [q, setQ] = useState('');
  const [sort, setSort] = useState('oldest'); // oldest | priority
  const [slaOnly, setSlaOnly] = useState(false);

  /* server-side query state — search + order + page (page 1 = fresh result set) */
  const [query, setQuery] = useState({ search: '', page: 1 });
  const [rows, setRows] = useState([]); // API customers, mapped
  const [meta, setMeta] = useState(null); // total, page, total_pages, averages…
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const order = sort === 'oldest' ? 'oldest' : 'newest';
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  /* debounce the search box → search param (name, email or client id) */
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery((prev) =>
        prev.search === q.trim() ? prev : { search: q.trim(), page: 1 },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  /* sort toggle / stage / window change → back to page 1 */
  useEffect(() => {
    setQuery((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [order, stageKey, windowDays]);

  const requestRef = useRef(0);
  const loadingRef = useRef(false);
  loadingRef.current = loading;

  const getStageQueue = useCallback(async () => {
    if (!stageKey) {
      setRows([]);
      setMeta(null);
      setError('This stage has no queue endpoint.');
      return;
    }
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: String(windowDays),
        limit: String(LIMIT),
        page: String(query.page),
        order,
      });
      if (query.search) params.set('search', query.search);

      const response = await GetAPIcalls(
        `${FUNNEL_STAGE_ENDPOINT(stageKey)}?${params.toString()}`,
        navigate,
      );
      if (requestId !== requestRef.current) return; // stale response
      if (response.status == 200 && response.data) {
        const data = response.data;
        const mapped = (data.customers || []).map(mapCase);
        setRows((prev) => {
          if (query.page === 1) return mapped;
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...mapped.filter((r) => !seen.has(r.id))];
        });
        setMeta(data);
        setError('');
      } else {
        setMeta(null);
        if (query.page === 1) setRows([]);
      }
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setMeta(null);
      if (query.page === 1) setRows([]);
      setError(err.message);
      console.error(err.message);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [stageKey, windowDays, order, query, navigate]);

  useEffect(() => {
    getStageQueue();
  }, [getStageQueue]);

  /* close on Esc */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = useMemo(() => {
    if (!slaOnly) return rows; // search/sort run on the server
    return rows.filter((c) => c.sla);
  }, [rows, slaOnly]);

  const slaCount = rows.filter((c) => c.sla).length;
  const queueTotal = meta?.total ?? rows.length;
  const hasMore = Boolean(meta?.has_more) && list.length > 0;

  /* infinite scroll inside the existing list container (no new UI) */
  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loadingRef.current) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      setQuery((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Panel */}
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl animate-slide-in flex flex-col">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${stage?.colors?.[0] || '#94A3B8'}22, ${stage?.colors?.[1] || '#CBD5E1'}44)`,
                  color: stage?.colors?.[0] || '#475569',
                }}
              >
                <Clock size={18} strokeWidth={1.9} />
              </span>
              <div className="min-w-0">
                <h3 className="font-bold text-slate-900 truncate">
                  {stage?.label} queue
                </h3>
                <p className="text-[11px] text-slate-400">
                  {queueTotal} {queueTotal === 1 ? 'case' : 'cases'}
                  {meta?.average_days_in_stage != null && (
                    <span className="tabular-nums">
                      {' '}
                      · avg {fmtDays(meta.average_days_in_stage)}d · oldest{' '}
                      {fmtDays(meta.oldest_days_in_stage)}d
                    </span>
                  )}
                  {/* {slaCount > 0 && (
                    <span className="text-[#B45309] font-bold">
                      {' '}
                      · {slaCount} at SLA risk
                    </span>
                  )} */}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            {stage?.desc}
          </p>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 space-y-2.5 bg-slate-50/60">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search case or customer…"
              className="w-full h-9 pl-9 pr-3 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:border-brand/40 focus:ring-4 focus:ring-brand/10 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSort(sort === 'oldest' ? 'latest' : 'oldest')}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-1 hover:border-slate-300 transition-all active:scale-95"
            >
              <ArrowUpDown size={11} />
              {sort === 'oldest' ? 'Oldest first' : 'Latest first'}
            </button>
            {/* <button
              onClick={() => setSlaOnly(!slaOnly)}
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 ring-1 transition-all active:scale-95 ${
                slaOnly
                  ? 'text-[#B45309] bg-[#FFFBEB] ring-[#FDE68A]'
                  : 'text-slate-600 bg-white ring-slate-200 hover:ring-slate-300'
              }`}
            >
              <AlertTriangle size={11} />
              SLA risk only
            </button> */}
          </div>
        </div>

        {/* Case list */}
        <div
          className="flex-1 overflow-y-auto px-5 py-3 space-y-2"
          onScroll={handleScroll}
        >
          {loading && rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm font-semibold text-slate-700">
                Loading cases…
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Fetching the {stage?.label} queue.
              </p>
            </div>
          ) : error && rows.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm font-semibold text-slate-700">
                Couldn’t load cases
              </p>
              <p className="text-xs text-slate-400 mt-1">{error}</p>
            </div>
          ) : list.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm font-semibold text-slate-700">
                {q.trim() || slaOnly
                  ? 'No cases match'
                  : 'No cases in this stage'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {q.trim() || slaOnly
                  ? 'Try clearing the search or filters.'
                  : `Nothing is sitting in ${stage?.label || 'this stage'} for the last ${windowDays} days.`}
              </p>
            </div>
          ) : (
            list.map((c, i) => (
              <div
                key={c.id}
                className="animate-fade-up group rounded-xl border border-slate-200/80 bg-white p-3 hover:border-brand/30 hover:shadow-card transition-all duration-200 cursor-pointer"
                style={{ animationDelay: `${(i % 25) * 40}ms` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-full ${avatarStyle(
                      c.name,
                    )} flex items-center justify-center text-xs font-semibold shrink-0`}
                  >
                    {initials(c.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {c.name}
                      </p>
                      {/* <span
                        className={`text-[9px] font-bold tracking-wide rounded px-1.5 py-0.5 shrink-0 ${
                          PRIORITY_CHIP[c.priority] || PRIORITY_CHIP.LOW
                        }`}
                      >
                        {c.priority}
                      </span> */}
                    </div>
                    <p className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                      {c.id}
                      <span className="inline-flex items-center gap-1">
                        <UserRound size={10} />
                        {c.assignee}
                      </span>
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p
                      className={`text-xs font-bold tabular-nums ${
                        c.sla ? 'text-[#DC2626]' : 'text-slate-600'
                      }`}
                    >
                      {fmtDays(c.days)}d in stage
                    </p>
                    {/* {c.sla && (
                      <p className="text-[10px] font-bold text-[#B45309]">
                        SLA risk
                      </p>
                    )} */}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* paging sentinel — keeps the existing list scrolling instead of adding controls */}
          {list.length > 0 && (hasMore || (loading && rows.length > 0)) && (
            <div className="py-3 text-center text-[11px] text-slate-400">
              {loading ? 'Loading more…' : 'Scroll for more…'}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
